import React, { useState } from 'react';
import { Filter } from './types';
import { FilterRenderer } from './FilterRenderer';
import { useFilters } from './FilterContext';
import { Button } from '@plerionhq/design-system';
import { cn } from '../../../lib/utils';

import { useTranslation } from 'react-i18next';

interface FilterBarProps {
  filters: Filter[];
  className?: string;
  showClearAll?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  className,
  showClearAll = true,
  collapsible = true,
  defaultCollapsed = false,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const { clearAll } = useFilters();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showAllFilters, setShowAllFilters] = useState(false);

  const hasActiveFilters = filters.some((filter) => {
    const value = filter.value;
    if (value === undefined || value === null) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (
      typeof value === 'object' &&
      value !== null &&
      'start' in value &&
      'end' in value
    ) {
      return value.start !== null || value.end !== null;
    }
    return true;
  });

  const visibleFilters = showAllFilters ? filters : filters.slice(0, 3);
  const hasMoreFilters = filters.length > 3;

  if (filters.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900',
        className,
      )}
    >
      {/* Filter Bar Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('filters')}
          </h3>
          {hasActiveFilters && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-[#FF732E] dark:bg-gray-700">
              {t('active', {
                length: filters.filter(
                  (f) => f.value !== undefined && f.value !== null,
                ).length,
              })}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {showClearAll && hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={clearAll}
              className="px-3 py-1 text-xs"
              label={t('clearAll')}
            />
          )}

          {collapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <svg
                className={cn(
                  'h-4 w-4 transition-transform',
                  isCollapsed ? 'rotate-180' : '',
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/* Filter Content */}
      {!isCollapsed && (
        <div className="p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visibleFilters.map((filter) => (
              <FilterRenderer
                key={filter.id}
                filter={filter}
                className="w-full"
              />
            ))}
          </div>

          {hasMoreFilters && !showAllFilters && (
            <div className="mt-4 text-center">
              <Button
                variant="secondary"
                onClick={() => setShowAllFilters(true)}
                className="px-3 py-1 text-xs"
                label={t('showMoreFilters', { count: filters.length - 3 })}
              />
            </div>
          )}

          {hasMoreFilters && showAllFilters && (
            <div className="mt-4 text-center">
              <Button
                variant="secondary"
                onClick={() => setShowAllFilters(false)}
                className="px-3 py-1 text-xs"
                label={t('showFewerFilters')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
